import { mapWizardStateToContract } from '@/domain/templates/template-mapper';
import type { WizardState } from '@/types/template';

/**
 * Exports the current builder state to a downloaded JSON file. Maps the local
 * builder shape to the persisted contract shape via `mapWizardStateToContract`,
 * wraps it in a JSON Blob, and triggers a download through a temporary anchor.
 *
 * The temporary anchor is the standard browser pattern for blob downloads —
 * `<a href={blobUrl} download>` then `.click()`. The object URL is revoked
 * immediately after the click to release the blob (browsers GC un-revoked blobs
 * eventually, but explicit cleanup is cheap and avoids leaks in long sessions).
 *
 * @param state - The current builder state.
 * @param fallbackName - Filename stem used when the template has no name.
 */
export function downloadTemplateJson(state: WizardState, fallbackName: string): void {
  const contract = mapWizardStateToContract(state);
  const blob = new Blob([JSON.stringify(contract, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${state.templateBasics.name || fallbackName}.json`;
  // Safari requires the anchor to be in the DOM for `download` to take effect;
  // Chrome/Firefox accept detached anchors but appending costs nothing.
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
